import {
  Box,
  Checkbox,
  CircularProgress,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Text,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Button } from "../../components/chackra-ui-button";
import { ProvinceSelect } from "../../components/province-select";
import { spanishProvinces } from "../../helpers/spanish-provinces";
import {
  useTaxProfileQuery,
  useRequestCompleteInvoiceMutation,
  useUpsertTaxProfileMutation,
} from "../../graphql/generated/user-v2-hooks";
import {
  isValidSpanishTaxId,
  normalizeTaxId,
} from "../../helpers/spanish-tax-id";
import { TFunction } from "i18next";
import * as Yup from "yup";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  invoiceId: string | null;
};

type FormValues = {
  nif: string;
  name: string;
  address: string;
  city: string;
  zipCode: string;
  province: string;
};

const validationSchema = (t: TFunction) =>
  Yup.object({
    nif: Yup.string()
      .required(t("taxProfile.fieldRequired"))
      .trim()
      .transform((value) =>
        typeof value === "string" ? normalizeTaxId(value) : value,
      )
      .test("nif-valid", t("taxProfile.nifInvalid"), (value) => {
        if (!value) return false;
        return isValidSpanishTaxId(value);
      }),
    name: Yup.string().required(t("taxProfile.fieldRequired")).trim(),
    address: Yup.string().required(t("taxProfile.fieldRequired")).trim(),
    city: Yup.string().required(t("taxProfile.fieldRequired")).trim(),
    zipCode: Yup.string().required(t("taxProfile.fieldRequired")).trim(),
    province: Yup.string()
      .required(t("taxProfile.fieldRequired"))
      .trim()
      .test("valid-province", t("taxProfile.provinceInvalid"), (value) =>
        value ? spanishProvinces.some((p) => p.code === value) : false,
      ),
  });

export const RequestInvoiceModal = (props: Props) => {
  const { t } = useTranslation();
  const toast = useToast();

  const { data: billingData, loading: billingLoading, error: billingError, refetch } =
    useTaxProfileQuery({
      context: { endpoint: "fitnextv2" },
      fetchPolicy: "network-only",
      skip: !props.isOpen,
    });

  const taxProfile = billingData?.taxProfile;

  const [updateProfile, setUpdateProfile] = useState(false);
  const [profileSaved, setProfileSaved] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: yupResolver(validationSchema(t)),
    reValidateMode: "onChange",
  });

  useEffect(() => {
    if (billingData) {
      reset({
        nif: taxProfile?.nif ?? "",
        name: taxProfile?.name ?? "",
        address: taxProfile?.address ?? "",
        city: taxProfile?.city ?? "",
        zipCode: taxProfile?.zipCode ?? "",
        province: taxProfile?.province ?? "",
      });
    }
  }, [billingData, taxProfile, reset]);

  useEffect(() => {
    if (!props.isOpen) {
      setProfileSaved(false);
      setUpdateProfile(false);
    }
  }, [props.isOpen]);

  const [requestComplete, { loading: requestLoading }] =
    useRequestCompleteInvoiceMutation({
      context: { endpoint: "fitnextv2" },
    });

  const [upsertTaxProfile, { loading: upsertLoading }] =
    useUpsertTaxProfileMutation({
      context: { endpoint: "fitnextv2" },
    });

  const isSubmitting = requestLoading || upsertLoading;

  const onSubmit = handleSubmit(async (values) => {
    if (!props.invoiceId) return;

    const clientData = {
      nif: normalizeTaxId(values.nif),
      name: values.name,
      address: values.address,
      city: values.city,
      zipCode: values.zipCode,
      province: values.province,
      country: "ES",
    };

    if (updateProfile && !profileSaved) {
      try {
        await upsertTaxProfile({ variables: { input: clientData } });
        setProfileSaved(true);
        toast({
          title: t("taxProfile.saved"),
          status: "success",
          isClosable: true,
        });
      } catch {
        toast({
          title: t("taxProfile.error"),
          status: "error",
          isClosable: true,
        });
        return;
      }
    }

    try {
      await requestComplete({
        variables: {
          input: {
            taxInvoiceId: props.invoiceId,
            client: clientData,
          },
        },
      });

      toast({
        title: t("taxInvoices.requestCompleteSuccess"),
        status: "success",
        isClosable: true,
      });
      props.onSuccess();
      props.onClose();
    } catch {
      toast({
        title: t("taxInvoices.requestCompleteError"),
        status: "error",
        isClosable: true,
      });
    }
  });

  return (
    <Modal isOpen={props.isOpen} onClose={props.onClose} size="xl">
      <ModalOverlay />
      <ModalContent borderRadius="12px" m={6}>
        <ModalCloseButton />
        <ModalHeader>
          <Text
            fontFamily="'Barlow Semi Condensed', sans-serif"
            fontWeight={600}
            fontSize="18px"
          >
            {t("taxInvoices.confirmTitle")}
          </Text>
        </ModalHeader>
        <ModalBody>
          {billingLoading ? (
            <HStack justify="center" py={4}>
              <CircularProgress isIndeterminate color="blue.500" size={4} />
            </HStack>
          ) : billingError ? (
            <VStack spacing={4} py={4} align="center">
              <Text fontSize="14px" color="red.500">
                {t("taxProfile.loadError")}
              </Text>
              <Button variant="senseiOutlined" onClick={() => refetch()}>
                {t("common.retry")}
              </Button>
            </VStack>
          ) : (
            <form id="request-invoice-form" onSubmit={onSubmit} noValidate autoComplete="off">
              <VStack spacing={4} align="start">
                {/* Info notice */}
                <Box w="full" bg="slate.50" borderRadius="10px" p={3}>
                  <Text fontSize="14px" color="slate.500" lineHeight="1.5">
                    {updateProfile
                      ? t("taxInvoices.updateDefaultProfileWarning")
                      : t("taxInvoices.confirmInfo")}
                  </Text>
                </Box>

                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} w="full">
                  <FormControl isInvalid={!!errors.nif} isRequired>
                    <FormLabel fontSize="16px" fontWeight={600} color="slate.600">
                      {t("taxProfile.nif")}
                    </FormLabel>
                    <Input
                      {...register("nif")}
                    />
                    {errors.nif?.message && (
                      <FormErrorMessage>{errors.nif.message}</FormErrorMessage>
                    )}
                  </FormControl>
                  <FormControl isInvalid={!!errors.name} isRequired>
                    <FormLabel fontSize="16px" fontWeight={600} color="slate.600">
                      {t("taxProfile.name")}
                    </FormLabel>
                    <Input
                      {...register("name")}
                    />
                    {errors.name?.message && (
                      <FormErrorMessage>{errors.name.message}</FormErrorMessage>
                    )}
                  </FormControl>
                </SimpleGrid>

                <FormControl isInvalid={!!errors.address} isRequired>
                  <FormLabel fontSize="16px" fontWeight={600} color="slate.600">
                    {t("taxProfile.address")}
                  </FormLabel>
                  <Input
                    {...register("address")}
                  />
                  {errors.address?.message && (
                    <FormErrorMessage>{errors.address.message}</FormErrorMessage>
                  )}
                </FormControl>

                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} w="full">
                  <FormControl isInvalid={!!errors.city} isRequired>
                    <FormLabel fontSize="16px" fontWeight={600} color="slate.600">
                      {t("taxProfile.city")}
                    </FormLabel>
                    <Input
                      {...register("city")}
                    />
                    {errors.city?.message && (
                      <FormErrorMessage>{errors.city.message}</FormErrorMessage>
                    )}
                  </FormControl>
                  <FormControl isInvalid={!!errors.zipCode} isRequired>
                    <FormLabel fontSize="16px" fontWeight={600} color="slate.600">
                      {t("taxProfile.zipCode")}
                    </FormLabel>
                    <Input
                      {...register("zipCode")}
                    />
                    {errors.zipCode?.message && (
                      <FormErrorMessage>{errors.zipCode.message}</FormErrorMessage>
                    )}
                  </FormControl>
                  <FormControl isInvalid={!!errors.province} isRequired>
                    <FormLabel fontSize="16px" fontWeight={600} color="slate.600">
                      {t("taxProfile.province")}
                    </FormLabel>
                    <Controller
                      name="province"
                      control={control}
                      render={({ field }) => (
                        <ProvinceSelect
                          id="modal-province"
                          value={field.value}
                          onChange={field.onChange}
                        />
                      )}
                    />
                    {errors.province?.message && (
                      <FormErrorMessage>{errors.province.message}</FormErrorMessage>
                    )}
                  </FormControl>
                </SimpleGrid>

                {/* Save as default checkbox */}
                <Box w="full" bg="slate.50" borderRadius="10px" p={3}>
                  <Checkbox
                    isChecked={updateProfile}
                    onChange={(e) => setUpdateProfile(e.target.checked)}
                    alignItems="flex-start"
                    spacing={3}
                    sx={{ ".chakra-checkbox__control": { mt: "2px" } }}
                  >
                    <Text fontSize="14px" color="slate.600" lineHeight="1.5">
                      {t("taxInvoices.updateDefaultProfile")}
                    </Text>
                  </Checkbox>
                </Box>
              </VStack>
            </form>
          )}
        </ModalBody>
        <ModalFooter>
          {!billingLoading && !billingError && (
            <HStack>
              <Button
                variant="senseiOutlined"
                onClick={props.onClose}
              >
                {t("common.cancel", "Cancelar")}
              </Button>
              <Button
                variant="senseiFilled"
                isLoading={isSubmitting}
                type="submit"
                form="request-invoice-form"
              >
                {t("taxInvoices.confirmRequest")}
              </Button>
            </HStack>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
